import { ContentCopy, Phone } from "@mui/icons-material";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";

import { useToast } from "~hooks/useToast";
import { formatOnlyDigits, formatPhoneNumberWithCountryCode } from "~utils/form/format";

const tCopyPhoneNumber = "Copy phone number";
const tCopiedToClipboard = "Phone number copied to clipboard";

export interface PhoneWithCopyIconProps {
  phoneNumber: string;
  /** Show as tel: link. Default true */
  asLink?: boolean;
  /** Show phone icon before number. Default true */
  showPhoneIcon?: boolean;
}

export const PhoneWithCopyIcon = ({ phoneNumber, asLink = true, showPhoneIcon = true }: PhoneWithCopyIconProps) => {
  const toast = useToast();
  const displayValue = formatPhoneNumberWithCountryCode(phoneNumber);
  const digitsOnly = formatOnlyDigits(phoneNumber);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!digitsOnly) {
      return;
    }
    try {
      await navigator.clipboard.writeText(digitsOnly);
      toast.success(tCopiedToClipboard);
    } catch {
      toast.error("Failed to copy phone number");
    }
  };

  const phoneContent = (
    <Box
      component={asLink ? "a" : "div"}
      href={asLink ? `tel:${phoneNumber}` : undefined}
      sx={{
        alignItems: "center",
        color: "primary.main",
        display: "flex",
        gap: 0.5,
        minWidth: 0,
        textDecoration: asLink ? "none" : undefined,
      }}
    >
      {showPhoneIcon && <Phone fontSize="small" sx={{ color: "inherit", flexShrink: 0 }} />}
      <Typography
        component="span"
        variant="body2"
        sx={{
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {displayValue}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        alignItems: "center",
        display: "flex",
        gap: 0.5,
      }}
    >
      {phoneContent}
      <Tooltip title={tCopyPhoneNumber}>
        <IconButton aria-label={tCopyPhoneNumber} onClick={handleCopy} size="small" sx={{ flexShrink: 0 }}>
          <ContentCopy fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};
